import React, { useState } from 'react';
import { 
  Table, Calendar, Trash2, ChevronUp, ChevronDown, X, Clock, 
  Scissors, AlertTriangle, CheckCircle, Zap, FileText, Edit2,
  Coffee, Utensils, HelpCircle 
} from 'lucide-react';
import { api } from '@/utils/api';
import { toast } from 'sonner';

// Format decimal minutes to MM:SS string (e.g., 17.62 -> "17:37")
// Each minute has 60 seconds, so 0.62 min = 37.2 seconds
const formatMinutesToMMSS = (decimalMinutes) => {
  if (decimalMinutes === null || decimalMinutes === undefined || isNaN(decimalMinutes)) return "0:00";
  const totalSeconds = Math.round(decimalMinutes * 60);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Format decimal minutes to HH:MM:SS string for longer durations
const formatMinutesToHHMMSS = (decimalMinutes) => {
  if (decimalMinutes === null || decimalMinutes === undefined || isNaN(decimalMinutes)) return "0:00:00";
  const totalSeconds = Math.round(decimalMinutes * 60);
  const hours = Math.floor(totalSeconds / 3600);
  const remainingSeconds = totalSeconds % 3600;
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Format with unit for display (shows both decimal and time format)
const formatTimeWithUnit = (decimalMinutes, showDetailed = false) => {
  if (decimalMinutes === null || decimalMinutes === undefined || isNaN(decimalMinutes)) return "-";
  if (showDetailed) {
    return `${decimalMinutes.toFixed(2)} min (${formatMinutesToMMSS(decimalMinutes)})`;
  }
  return `${formatMinutesToMMSS(decimalMinutes)}`;
};

const STOPPING_REASONS = [
  "Pausa Almoço",
  "Pausa Manhã",
  "Pausa Tarde",
  "Pausa Café",
  "Avaria Máquina",
  "Troca de Referência",
  "Falta de Material",
  "Manutenção",
  "Limpeza",
  "Formação",
  "Reunião",
  "Fim de Turno",
  "Transição de Turno",
  "Outro"
];

// Time Adjustments Summary Component - Shows all deductions applied to the day
function TimeAdjustmentsSummary({ metrics, records, hasOvertime, overtimeStartIndex, t, finalValues }) {
  // Extract adjustment data from metrics
  const breakDeductMachineStopped = metrics.break_deduct_machine_stopped || 0;
  const breakDeductBetweenMarkers = metrics.break_deduct_between_markers || 0;
  const overtimeGapMinutes = metrics.overtime_gap_minutes || 0;
  const breaksInfo = metrics.breaks_info || {};
  
  // Raw totals (before any deductions)
  const rawMachineStopped = metrics.total_machine_stopped_raw || metrics.total_machine_stopped || 0;
  const rawEntreCortes = metrics.total_entre_cortes || 0;
  
  // Manual adjustments
  const manualAdjStopped = metrics.manual_adj_machine_stopped || 0;
  const manualAdjBetween = metrics.manual_adj_between_markers || 0;
  
  // Check if breaks were already pre-deducted at import
  const summaryRealRecs = records.filter(r => !r.is_time_adjustment);
  const alreadyDeducted = summaryRealRecs.some(r => (r.break_deducted_from_stopped || 0) > 0 || (r.break_deducted_from_between || 0) > 0);
  
  // Final totals (matching the table/summary exactly)
  let finalMachineStopped, finalEntreCortes;
  if (alreadyDeducted) {
    finalMachineStopped = Math.max(0, rawMachineStopped - manualAdjStopped);
    finalEntreCortes = Math.max(0, rawEntreCortes - manualAdjBetween);
  } else {
    finalMachineStopped = Math.max(0, rawMachineStopped - breakDeductMachineStopped - manualAdjStopped);
    finalEntreCortes = Math.max(0, rawEntreCortes - breakDeductBetweenMarkers - manualAdjBetween);
  }
  
  // Calculate percentages for the distribution bar (matching table)
  const totalCuttingTime = metrics.total_cutting_time || 0;
  const totalFinal = totalCuttingTime + finalMachineStopped + finalEntreCortes;
  const pctCorte = totalFinal > 0 ? (totalCuttingTime / totalFinal * 100) : 0;
  const pctStopped = totalFinal > 0 ? (finalMachineStopped / totalFinal * 100) : 0;
  const pctEntre = totalFinal > 0 ? (finalEntreCortes / totalFinal * 100) : 0;
  
  // Check if there are any adjustments
  const totalDeductions = breakDeductMachineStopped + breakDeductBetweenMarkers;
  const hasBreakAdjustments = totalDeductions > 0;
  const hasOvertimeAdjustment = hasOvertime && overtimeGapMinutes > 0;
  const hasAnyAdjustments = hasBreakAdjustments || hasOvertimeAdjustment || Object.keys(breaksInfo).length > 0;
  
  // Calculate shift transition gap from record before overtime
  let shiftTransitionGap = 0;
  if (hasOvertime && overtimeStartIndex > 0 && records[overtimeStartIndex - 1]) {
    shiftTransitionGap = parseFloat(records[overtimeStartIndex - 1].between_markers || 0);
  }
  
  // Build break deductions list - show only the 3 standard legal breaks
  const breakDeductions = [];
  const realRecs = records.filter(r => !r.is_time_adjustment);
  
  // Use breaks_info from metrics (detected by the day's records)
  Object.entries(breaksInfo).forEach(([key, info]) => {
    if (info && info.detected) {
      const recordIdx = info.record_indices?.[0] || 0;
      const record = realRecs[recordIdx];
      breakDeductions.push({
        name: info.name,
        minutes: info.deducted || 0,
        from: info.deducted_from === 'machine_stopped' ? 'Máq. Parada' : 'Entre Tarefas',
        recordIndex: recordIdx,
        time: record ? `${record.start_time} → ${record.end_time}` : ''
      });
    }
  });
  
  // If no breaks_info, check for legacy break_type in records (one per type max)
  if (breakDeductions.length === 0) {
    const seenTypes = new Set();
    realRecs.forEach((r, idx) => {
      if (r.break_type && !seenTypes.has(r.break_type)) {
        seenTypes.add(r.break_type);
        const deduction = (r.break_deducted_from_stopped || 0) + (r.break_deducted_from_between || 0);
        if (deduction > 0) {
          const breakNames = {
            'morning': 'Pausa Manhã (10:00-10:10)',
            'lunch': 'Almoço (12:00-13:30)',
            'afternoon': 'Pausa Tarde (16:00-16:10)'
          };
          breakDeductions.push({
            name: breakNames[r.break_type] || r.break_type,
            minutes: deduction,
            from: r.break_deducted_from_stopped > 0 ? 'Máq. Parada' : 'Entre Tarefas',
            recordIndex: idx,
            time: `${r.start_time} → ${r.end_time}`
          });
        }
      }
    });
  }
  
  // Collect anomalous classifications from records
  const anomalousClassifications = [];
  records.forEach((r, idx) => {
    if (r.anomalous_classification_stopped) {
      anomalousClassifications.push({
        recordIndex: idx,
        type: 'Máq. Parada',
        classification: r.anomalous_classification_stopped,
        reason: r.anomalous_reason_stopped || r.anomalous_classification_stopped,
        time: `${r.start_time} → ${r.end_time}`,
        minutes: parseFloat(r.anomalous_minutes_stopped || r.machine_stopped || 0)
      });
    }
    if (r.anomalous_classification_between) {
      anomalousClassifications.push({
        recordIndex: idx,
        type: 'Entre Marcadas',
        classification: r.anomalous_classification_between,
        reason: r.anomalous_reason_between || r.anomalous_classification_between,
        time: `${r.start_time} → ${r.end_time}`,
        minutes: parseFloat(r.anomalous_minutes_between || r.between_markers || 0)
      });
    }
  });

  if (!hasAnyAdjustments && breakDeductions.length === 0 && anomalousClassifications.length === 0) {
    return null;
  }

  return (
    <div className={`mb-4 p-4 rounded-lg ${t.inner} border ${t.divider}`}>
      <div className="flex items-center gap-2 mb-3">
        <Clock className="w-4 h-4 text-emerald-500" />
        <h4 className={`text-sm font-semibold ${t.text}`}>Ajustes de Tempo Aplicados</h4>
      </div>
      
      <div className={`grid grid-cols-1 ${anomalousClassifications.length > 0 ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-4 text-xs`}>
        {/* Left Column - Break Deductions */}
        <div>
          <div className={`font-medium ${t.textMut} mb-2 flex items-center gap-1`}>
            <Coffee className="w-3 h-3" />
            Pausas Legais Deduzidas
          </div>
          
          {breakDeductions.length > 0 ? (
            <div className="space-y-1.5">
              {breakDeductions.map((bd, i) => (
                <div key={`${bd.name}-${bd.recordIndex}`} className={`flex justify-between items-center p-2 rounded ${t.card}`}>
                  <div>
                    <span className={`font-medium ${t.text}`}>{bd.name}</span>
                    <span className={`block text-[10px] ${t.textMut}`}>
                      Reg. #{bd.recordIndex + 1} • {bd.time} • de {bd.from}
                    </span>
                  </div>
                  <span className="text-emerald-500 font-bold" title={`${bd.minutes.toFixed(2)} min`}>
                    -{formatMinutesToMMSS(bd.minutes)}
                  </span>
                </div>
              ))}
              <div className={`flex justify-between items-center pt-2 border-t ${t.divider}`}>
                <span className={`font-medium ${t.textMut}`}>Total Pausas:</span>
                <span className="text-emerald-500 font-bold" title={`${(breakDeductMachineStopped + breakDeductBetweenMarkers).toFixed(2)} min`}>
                  -{formatMinutesToHHMMSS(breakDeductMachineStopped + breakDeductBetweenMarkers)}
                </span>
              </div>
            </div>
          ) : (
            <div className={`p-2 rounded ${t.card} ${t.textMut}`}>
              Nenhuma pausa legal detectada/confirmada
            </div>
          )}
        </div>
        
        {/* Middle Column - Anomalous Classifications */}
        {anomalousClassifications.length > 0 && (
          <div>
            <div className={`font-medium ${t.textMut} mb-2 flex items-center gap-1`}>
              <AlertTriangle className="w-3 h-3 text-amber-500" />
              Classificações Anormais
            </div>
            <div className="space-y-1.5">
              {anomalousClassifications.map((ac, i) => (
                <div key={`anom-${ac.recordIndex}-${i}`} className={`flex justify-between items-center p-2 rounded ${t.card}`}>
                  <div>
                    <span className={`font-medium ${t.text}`}>{ac.reason}</span>
                    <span className={`block text-[10px] ${t.textMut}`}>
                      Reg. #{ac.recordIndex + 1} • {ac.time} • {ac.type}
                    </span>
                  </div>
                  <span className={`font-bold ${ac.classification === 'exclude' ? 'text-red-400' : 'text-amber-500'}`} title={`${ac.minutes.toFixed(2)} min`}>
                    {ac.classification === 'exclude' ? '-' : ''}{formatMinutesToMMSS(ac.minutes)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Right Column - Overtime Transition & Summary */}
        <div>
          {hasOvertimeAdjustment && (
            <div className="mb-3">
              <div className={`font-medium ${t.textMut} mb-2 flex items-center gap-1`}>
                <Zap className="w-3 h-3 text-amber-500" />
                Transição de Turno
              </div>
              <div className={`p-2 rounded ${t.card}`}>
                <div className="flex justify-between items-center">
                  <div>
                    <span className={`font-medium ${t.text}`}>Pausa entre turnos</span>
                    <span className={`block text-[10px] ${t.textMut}`}>
                      Fim turno normal → Início turno extra
                    </span>
                  </div>
                  <span className="text-amber-500 font-bold" title={`${shiftTransitionGap.toFixed(2)} min`}>
                    -{formatMinutesToHHMMSS(shiftTransitionGap)}
                  </span>
                </div>
              </div>
            </div>
          )}
          
          {/* Totals Summary */}
          <div className={`font-medium ${t.textMut} mb-2`}>Resumo</div>
          <div className={`p-3 rounded ${t.card} space-y-2`}>
            <div className="flex justify-between">
              <span className={t.textMut}>Máq. Parada (bruto):</span>
              <span className={`font-medium ${t.text}`} title={`${rawMachineStopped.toFixed(2)} min`}>
                {formatMinutesToHHMMSS(rawMachineStopped)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className={t.textMut}>Entre Tarefas (bruto):</span>
              <span className={`font-medium ${t.text}`} title={`${rawEntreCortes.toFixed(2)} min`}>
                {formatMinutesToHHMMSS(rawEntreCortes)}
              </span>
            </div>
            <div className={`flex justify-between pt-2 border-t ${t.divider}`}>
              <span className={t.textMut}>Deduções pausas:</span>
              <span className="text-emerald-500 font-medium" title={`${totalDeductions.toFixed(2)} min`}>
                -{formatMinutesToHHMMSS(totalDeductions)}
              </span>
            </div>
            {hasOvertimeAdjustment && (
              <div className="flex justify-between">
                <span className={t.textMut}>Transição turno:</span>
                <span className="text-amber-500 font-medium" title={`${shiftTransitionGap.toFixed(2)} min`}>
                  -{formatMinutesToHHMMSS(shiftTransitionGap)}
                </span>
              </div>
            )}
            <div className={`flex justify-between pt-2 border-t-2 ${t.divider} font-bold`}>
              <span className={t.text}>Máq. Parada (final):</span>
              <span className="text-red-400" title={`${(finalValues?.totalMachineStopped ?? finalMachineStopped).toFixed(2)} min`}>
                {formatMinutesToHHMMSS(finalValues?.totalMachineStopped ?? finalMachineStopped)}
              </span>
            </div>
            <div className="flex justify-between font-bold">
              <span className={t.text}>Entre Tarefas (final):</span>
              <span className="text-cyan-400" title={`${(finalValues?.totalEntreCortes ?? finalEntreCortes).toFixed(2)} min`}>
                {formatMinutesToHHMMSS(finalValues?.totalEntreCortes ?? finalEntreCortes)}
              </span>
            </div>
            {manualAdjStopped > 0 && (
              <div className="flex justify-between text-violet-400">
                <span>Ajuste manual (Parada):</span>
                <span>-{formatMinutesToMMSS(manualAdjStopped)}</span>
              </div>
            )}
            {manualAdjBetween > 0 && (
              <div className="flex justify-between text-violet-400">
                <span>Ajuste manual (Entre):</span>
                <span>-{formatMinutesToMMSS(manualAdjBetween)}</span>
              </div>
            )}
            {/* Final distribution */}
            <div className={`pt-2 border-t ${t.divider}`}>
              <div className={`text-[10px] ${t.textMut} mb-1`}>Distribuição Final:</div>
              <div className="flex items-center gap-2">
                <span className="text-emerald-500 font-bold text-xs">{(finalValues?.pctCorte ?? pctCorte).toFixed(1)}%</span>
                <div className="flex-1 h-2 rounded overflow-hidden flex">
                  <div className="bg-emerald-500 h-full" style={{ width: `${finalValues?.pctCorte ?? pctCorte}%` }} />
                  <div className="bg-red-500 h-full" style={{ width: `${finalValues?.pctStopped ?? pctStopped}%` }} />
                  <div className="bg-cyan-500 h-full" style={{ width: `${finalValues?.pctEntre ?? pctEntre}%` }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Daily Analysis View with Full Data Table
export function DailyAnalysisView({ dailySummary, selectedDay, onSelectDay, onDeleteDay, settings, onRefresh, t }) {
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedDays, setSelectedDays] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [dayToDelete, setDayToDelete] = useState(null);
  const [periodView, setPeriodView] = useState('day'); // 'day', 'week', 'range'
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Modal state for classifying anomalous times
  const [showClassifyModal, setShowClassifyModal] = useState(false);
  const [classifyRecord, setClassifyRecord] = useState(null);
  const [classifyMinutes, setClassifyMinutes] = useState(0);
  const [classifyReason, setClassifyReason] = useState('');
  const [classifying, setClassifying] = useState(false);
  const [showFormulas, setShowFormulas] = useState(false);
  
  if (!dailySummary || dailySummary.length === 0) {
    return (
      <div className={`${t.card} rounded-lg p-6 text-center`}>
        <p className={t.textMut}>Nenhum dado diário disponível.</p>
      </div>
    );
  }
  
  const efficiencyTarget = settings?.efficiencyTarget || 60;
  const efficiencyWarning = settings?.efficiencyWarning || 45;
  const machineStoppedTarget = settings?.machineStoppedTarget || 25;
  const machineStoppedWarning = settings?.machineStoppedWarning || 35;
  const entreCorteTarget = settings?.entreCorteTarget || 2;
  const entreCorteWarning = settings?.entreCorteWarning || 3;
  const speedTarget = settings?.speedTarget || 11;
  const speedWarning = settings?.speedWarning || 9;
  
  // Helper function to get color class based on value and thresholds
  // For "higher is better" metrics (efficiency, speed)
  const getColorHigherBetter = (value, greenThreshold, yellowThreshold) => {
    if (value >= greenThreshold) return 'text-emerald-400';
    if (value >= yellowThreshold) return 'text-amber-400';
    return 'text-red-400';
  };
  
  // For "lower is better" metrics (% parada, % entre, tempo entre)
  const getColorLowerBetter = (value, greenThreshold, yellowThreshold) => {
    if (value <= greenThreshold) return 'text-emerald-400';
    if (value <= yellowThreshold) return 'text-amber-400';
    return 'text-red-400';
  };
  
  // Get configured reasons for classification dropdown
  const classifyReasons = (() => {
    if (settings?.anomalousReasons?.length > 0) return settings.anomalousReasons;
    // Fallback to default anomalous reasons from SettingsPanel defaults
    return [
      { id: 'breakdown', name: 'Avaria' },
      { id: 'material', name: 'Falta de Material' },
      { id: 'setup', name: 'Setup/Preparação' },
      { id: 'maintenance', name: 'Manutenção Preventiva' },
      { id: 'operator', name: 'Ausência Operador' },
      { id: 'quality', name: 'Controlo Qualidade' },
      { id: 'lunch', name: 'Pausa Almoço' },
      { id: 'training', name: 'Formação' },
      { id: 'shift_end', name: 'Fim de Turno' },
      { id: 'other', name: 'Outro' },
    ];
  })();

  // Handle classify anomalous time
  const handleOpenClassify = (record, day) => {
    setClassifyRecord({ ...record, date: day.date });
    const originalValue = Math.round(parseFloat(record.machine_stopped || record.between_markers || record.original_value || 0));
    setClassifyMinutes(originalValue);
    // Use first configured reason
    setClassifyReason(classifyReasons[0]?.name || '');
    setShowClassifyModal(true);
  };
  
  const handleClassify = async () => {
    if (!classifyRecord || !classifyReason || classifyMinutes <= 0) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    setClassifying(true);
    try {
      const originalValue = parseFloat(classifyRecord.machine_stopped || classifyRecord.between_markers || classifyRecord.original_value || 0);
      
      // Call API to classify the record - format matching backend ClassifyAnomalousRequest
      const response = await api.post('/cutting-simulator/anomalous/classify', {
        classifications: [{
          record_index: classifyRecord.record_index,
          date: classifyRecord.date,
          classification: 'exclude', // Exclude from calculations
          reason: classifyReason,
          field_type: classifyRecord.field_type,
          original_value: originalValue
        }]
      });
      
      // Check if the classification was successful
      if (response.data?.results?.[0]?.status === 'not_found') {
        toast.error(`Os dados do dia ${classifyRecord.date} ainda não foram confirmados. Vá ao separador "Importações" e confirme a importação primeiro.`, { duration: 6000 });
        setClassifying(false);
        return;
      }
      
      toast.success(`${classifyMinutes} min classificados como "${classifyReason}"`);
      setShowClassifyModal(false);
      setClassifyRecord(null);
      
      // Refresh data
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao classificar registo');
    } finally {
      setClassifying(false);
    }
  };
  
  // Calculate expected end time based on settings
  const parseTime = (timeStr) => {
    if (!timeStr) return 0;
    const [h, m] = timeStr.split(':').map(Number);
    return h * 60 + m;
  };
  
  const expectedEndMinutes = parseTime(settings?.afternoonEnd || "17:30");
  
  // Filter data based on period view
  const getFilteredData = () => {
    let filtered = [...dailySummary];
    
    if (periodView === 'week') {
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay() + 1);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      
      filtered = dailySummary.filter(d => {
        const date = new Date(d.date);
        return date >= startOfWeek && date <= endOfWeek;
      });
    } else if (periodView === 'range' && dateRange.start && dateRange.end) {
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      
      filtered = dailySummary.filter(d => {
        const date = new Date(d.date);
        return date >= startDate && date <= endDate;
      });
    }
    
    return filtered;
  };
  
  const filteredData = getFilteredData();
  
  // Sort data
  const sorted = [...filteredData].sort((a, b) => {
    const aVal = a[sortBy] || 0;
    const bVal = b[sortBy] || 0;
    if (sortDir === 'asc') return aVal > bVal ? 1 : -1;
    return aVal < bVal ? 1 : -1;
  });

  // Pagination
  const totalPages = Math.ceil(sorted.length / pageSize);
  const paginatedData = sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  
  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  };
  
  const formatDateWithDay = (dateStr) => {
    const d = new Date(dateStr);
    const dayName = d.toLocaleDateString('pt-PT', { weekday: 'short' }).replace('.', '');
    const dayNum = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    // Format: DD/MM/YYYY DayName (date first for proper sorting)
    return `${dayNum}/${month}/${year} ${dayName.charAt(0).toUpperCase()}${dayName.slice(1)}`;
  };
  
  // Calculate overtime for a day
  const calculateOvertime = (day) => {
    if (!day.last_end) return null;
    const endMinutes = parseTime(day.last_end);
    const diff = endMinutes - expectedEndMinutes;
    if (diff > 5) return diff;
    return null;
  };
  
  // Toggle day selection for bulk delete
  const toggleDaySelection = (date) => {
    setSelectedDays(prev => 
      prev.includes(date) ? prev.filter(d => d !== date) : [...prev, date]
    );
  };
  
  // Select/deselect all
  const toggleSelectAll = () => {
    if (selectedDays.length === paginatedData.length) {
      setSelectedDays([]);
    } else {
      setSelectedDays(paginatedData.map(d => d.date));
    }
  };
  
  // Handle delete confirmation
  const confirmDelete = (date) => {
    setDayToDelete(date);
    setShowDeleteConfirm(true);
  };
  
  // Handle bulk delete confirmation
  const confirmBulkDelete = () => {
    setDayToDelete(null);
    setShowDeleteConfirm(true);
  };
  
  // Execute delete
  const executeDelete = async () => {
    if (dayToDelete) {
      await onDeleteDay(dayToDelete);
    } else if (selectedDays.length > 0) {
      for (const date of selectedDays) {
        await onDeleteDay(date);
      }
      setSelectedDays([]);
    }
    setShowDeleteConfirm(false);
    setDayToDelete(null);
  };
  
  // Sort indicator
  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ChevronUp className="w-3 h-3 opacity-30" />;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  return (
    <div className="space-y-4" data-testid="daily-analysis">
      {/* Classify Modal */}
      {showClassifyModal && classifyRecord && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`${t.card} rounded-lg p-6 max-w-md mx-4 w-full`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <Edit2 className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <h3 className={`text-lg font-semibold ${t.text}`}>Classificar Tempo</h3>
                <p className={`text-sm ${t.textMut}`}>
                  {classifyRecord.field_type === 'machine_stopped' ? 'Máquina Parada' : 'Entre Marcadas'} - {classifyRecord.start_time} → {classifyRecord.end_time}
                </p>
              </div>
            </div>
            
            <div className="space-y-4">
              {/* Original value */}
              <div className={`p-3 rounded-lg ${t.inner}`}>
                <p className={`text-sm ${t.textMut}`}>Tempo Original</p>
                <p className={`text-2xl font-bold ${classifyRecord.field_type === 'machine_stopped' ? 'text-red-500' : 'text-cyan-500'}`}>
                  {parseFloat(classifyRecord.machine_stopped || classifyRecord.between_markers || classifyRecord.original_value || 0).toFixed(1)} min
                </p>
              </div>
              
              {/* Minutes to extract */}
              <div>
                <label className={`block text-sm font-medium ${t.text} mb-2`}>
                  Minutos a Extrair
                </label>
                <input
                  type="number"
                  value={classifyMinutes}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || val === '0') {
                      setClassifyMinutes(0);
                    } else {
                      const num = parseInt(val);
                      if (!isNaN(num)) {
                        const max = Math.ceil(parseFloat(classifyRecord.machine_stopped || classifyRecord.between_markers || classifyRecord.original_value || 0));
                        setClassifyMinutes(Math.min(Math.max(0, num), max));
                      }
                    }
                  }}
                  onFocus={(e) => e.target.select()}
                  min={0}
                  max={Math.ceil(parseFloat(classifyRecord.machine_stopped || classifyRecord.between_markers || classifyRecord.original_value || 0))}
                  className={`w-full px-4 py-2 rounded-lg ${t.inputBg} ${t.card} ${t.text}`}
                />
                <p className={`text-xs ${t.textMut} mt-1`}>
                  Restante após extração: {(parseFloat(classifyRecord.machine_stopped || classifyRecord.between_markers || classifyRecord.original_value || 0) - classifyMinutes).toFixed(1)} min
                </p>
              </div>
              
              {/* Reason */}
              <div>
                <label className={`block text-sm font-medium ${t.text} mb-2`}>
                  Motivo da Paragem
                </label>
                <select
                  value={classifyReason}
                  onChange={(e) => setClassifyReason(e.target.value)}
                  className={`w-full px-4 py-2 rounded-lg ${t.inputBg} ${t.card} ${t.text}`}
                >
                  {classifyReasons.map(reason => (
                    <option key={reason.id || reason.name} value={reason.name}>{reason.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowClassifyModal(false); setClassifyRecord(null); }}
                className={`px-4 py-2 rounded-lg ${t.inner} ${t.textSec}`}
              >
                Cancelar
              </button>
              <button
                onClick={handleClassify}
                disabled={classifying || classifyMinutes <= 0}
                className="px-4 py-2 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
              >
                {classifying ? 'A guardar...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`${t.card} rounded-lg p-6 max-w-md mx-4`}>
            <h3 className={`text-lg font-semibold ${t.text} mb-4`}>Confirmar Eliminação</h3>
            <p className={`${t.textMut} mb-6`}>
              {dayToDelete 
                ? `Tem a certeza que deseja eliminar os dados de ${formatDateWithDay(dayToDelete)}?`
                : `Tem a certeza que deseja eliminar ${selectedDays.length} dia(s) selecionado(s)?`
              }
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowDeleteConfirm(false); setDayToDelete(null); }}
                className={`px-4 py-2 rounded-lg ${t.inner} ${t.textSec}`}
              >
                Cancelar
              </button>
              <button
                onClick={executeDelete}
                className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header with period filter */}
      <div className={`${t.card} rounded-lg p-4`}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Table className="w-5 h-5 text-sky-500" />
            <h3 className={`text-sm font-semibold ${t.text} uppercase tracking-wider`}>
              Análise Diária
            </h3>
            <span className={`text-sm ${t.textMut}`}>
              ({filteredData.length} dia{filteredData.length !== 1 ? 's' : ''})
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFormulas(!showFormulas)}
              className={`flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg transition-colors ${showFormulas ? 'bg-sky-500 text-white' : `${t.inner} ${t.textMut} hover:text-sky-500`}`}
              title="Ver fórmulas de cálculo"
            >
              <HelpCircle className="w-3.5 h-3.5" />
              Fórmulas
            </button>
            
            {selectedDays.length > 0 && (
              <button
                onClick={confirmBulkDelete}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-red-500/10 text-red-500 rounded-lg hover:bg-red-500/20"
              >
                <Trash2 className="w-3 h-3" />
                Eliminar ({selectedDays.length})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Data Table */}
      {/* Formulas Help Panel */}
      {showFormulas && (
        <div className={`${t.card} rounded-lg p-5 mb-4 border-l-4 border-sky-500`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-sm font-bold ${t.text} flex items-center gap-2`}>
              <HelpCircle className="w-4 h-4 text-sky-500" />
              Fórmulas de Cálculo da Tabela
            </h3>
            <button onClick={() => setShowFormulas(false)} className={`p-1 rounded ${t.inner} hover:opacity-80`}>
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
            <div className={`p-3 rounded-lg ${t.inner}`}>
              <div className="font-semibold text-emerald-400 mb-1">% Corte (Eficiência)</div>
              <div className={t.textMut}>Tempo de Corte / (T.Corte + T.Intra Ajust.) x 100</div>
              <div className={`mt-1 ${t.text} font-mono text-[10px]`}>Eficiência = quanto tempo a máquina corta vs quanto está parada</div>
            </div>
            <div className={`p-3 rounded-lg ${t.inner}`}>
              <div className="font-semibold text-red-400 mb-1">% Intra Marcadas</div>
              <div className={t.textMut}>T.Intra Ajustado / (T.Corte + T.Intra Ajust. + T.Entre Ajust.) x 100</div>
              <div className={`mt-1 ${t.text} font-mono text-[10px]`}>Percentagem do tempo total gasto parado dentro das marcadas</div>
            </div>
            <div className={`p-3 rounded-lg ${t.inner}`}>
              <div className="font-semibold text-cyan-400 mb-1">% Entre Marcadas</div>
              <div className={t.textMut}>T.Entre Ajustado / (T.Corte + T.Intra Ajust. + T.Entre Ajust.) x 100</div>
              <div className={`mt-1 ${t.text} font-mono text-[10px]`}>Percentagem do tempo total gasto entre marcadas consecutivas</div>
            </div>
            <div className={`p-3 rounded-lg ${t.inner}`}>
              <div className="font-semibold text-sky-400 mb-1">T.Corte</div>
              <div className={t.textMut}>Soma do tempo efetivo de corte de todos os registos do dia (sem ajustes)</div>
            </div>
            <div className={`p-3 rounded-lg ${t.inner}`}>
              <div className="font-semibold text-amber-400 mb-1">T.Intra Marc. (ajustado)</div>
              <div className={t.textMut}>Tempo total parado dentro das marcadas - pausas legais detectadas</div>
              <div className={`mt-1 ${t.text} font-mono text-[10px]`}>Pausas: Manhã 10min, Almoço 90min, Tarde 10min</div>
            </div>
            <div className={`p-3 rounded-lg ${t.inner}`}>
              <div className="font-semibold text-cyan-400 mb-1">T.Entre Marc. (ajustado)</div>
              <div className={t.textMut}>Tempo total entre marcadas consecutivas - pausas legais detectadas nos intervalos</div>
            </div>
            <div className={`p-3 rounded-lg ${t.inner}`}>
              <div className="font-semibold text-cyan-400 mb-1">Méd.E.Marc</div>
              <div className={t.textMut}>T.Entre Marc. / Número de Cortes</div>
              <div className={`mt-1 ${t.text} font-mono text-[10px]`}>Média de tempo entre cada marcada</div>
            </div>
            <div className={`p-3 rounded-lg ${t.inner}`}>
              <div className="font-semibold text-violet-400 mb-1">Deduções</div>
              <div className={t.textMut}>Total de pausas legais removidas dos tempos brutos</div>
              <div className={`mt-1 ${t.text} font-mono text-[10px]`}>= Dedução Intra + Dedução Entre</div>
            </div>
            <div className={`p-3 rounded-lg ${t.inner}`}>
              <div className={`font-semibold ${t.text} mb-1`}>Vel.Média</div>
              <div className={t.textMut}>Média aritmética das velocidades reais de cada registo</div>
              <div className={`mt-1 ${t.text} font-mono text-[10px]`}>= (V1 + V2 + ... + Vn) / N</div>
            </div>
            <div className={`p-3 rounded-lg ${t.inner}`}>
              <div className="font-semibold text-emerald-400 mb-1">Vel.Real (Ponderada)</div>
              <div className={t.textMut}>Média ponderada: cada velocidade multiplicada pela fração do tempo de corte</div>
              <div className={`mt-1 ${t.text} font-mono text-[10px]`}>= Sum(Vi x Ti) / Sum(Ti)</div>
            </div>
            <div className={`p-3 rounded-lg ${t.inner}`}>
              <div className={`font-semibold ${t.text} mb-1`}>Perímetro</div>
              <div className={t.textMut}>Soma total do perímetro cortado em todos os registos do dia</div>
            </div>
            <div className={`p-3 rounded-lg ${t.inner}`}>
              <div className="font-semibold text-amber-400 mb-1">Fora Hor.</div>
              <div className={t.textMut}>Tempo trabalhado fora do horário (antes 8:00 + após 17:30). Para turnos extra, desconta o gap (pausa jantar)</div>
            </div>
            <div className={`p-3 rounded-lg ${t.inner}`}>
              <div className="font-semibold text-sky-400 mb-1">T.Total</div>
              <div className={t.textMut}>Tempo corrido entre primeiro e último registo menos pausas legais e gap de turno extra</div>
              <div className={`mt-1 ${t.text} font-mono text-[10px]`}>= (Último - Primeiro) - 110min - gap turno extra</div>
            </div>
          </div>
        </div>
      )}
      
      <div className={`${t.card} rounded-lg overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className={`w-full text-sm ${t.text}`}>
            <thead>
              <tr className={`${t.inner} border-b ${t.divider} text-xs uppercase tracking-wider`}>
                <th className="py-3 px-2 w-8">
                  <input
                    type="checkbox"
                    checked={selectedDays.length === paginatedData.length && paginatedData.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded"
                  />
                </th>
                <th 
                  className="text-left py-3 px-2 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5"
                  onClick={() => toggleSort('date')}
                >
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3 text-sky-500" />
                    Data <SortIcon col="date" />
                  </div>
                </th>
                <th 
                  className="text-center py-3 px-2 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5"
                  onClick={() => toggleSort('total_cuts')}
                >
                  <div className="flex items-center justify-center gap-1">
                    Cortes <SortIcon col="total_cuts" />
                  </div>
                </th>
                
                {/* PERCENTAGENS - agrupadas com fundo */}
                <th 
                  className="text-center py-3 px-2 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 bg-emerald-500/10 border-l border-emerald-500/30"
                  onClick={() => toggleSort('efficiency')}
                  title="% do tempo útil com máquina a cortar"
                >
                  <div className="flex items-center justify-center gap-1 text-emerald-400">
                    % Corte <SortIcon col="efficiency" />
                  </div>
                </th>
                <th 
                  className="text-center py-3 px-2 bg-red-500/10" 
                  title="% do tempo útil com máquina parada"
                >
                  <span className="text-red-400">% Intra Marc.</span>
                </th>
                <th 
                  className="text-center py-3 px-2 bg-cyan-500/10 border-r border-cyan-500/30" 
                  title="% do tempo útil entre tarefas"
                >
                  <span className="text-cyan-400">% Entre Marc.</span>
                </th>
                
                {/* TEMPOS - agrupados */}
                <th 
                  className="text-center py-3 px-2 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5"
                  onClick={() => toggleSort('total_cutting_time')}
                  title="Tempo de corte"
                >
                  <div className="flex items-center justify-center gap-1 text-sky-400">
                    T.Corte <SortIcon col="total_cutting_time" />
                  </div>
                </th>
                <th className="text-center py-3 px-2" title="Tempo parado (ajustado)">
                  <span className="text-amber-400">T.Intra Marc.</span>
                </th>
                <th className="text-center py-3 px-2" title="Tempo entre operações (ajustado)">
                  <span className="text-cyan-400">T.Entre Marc.</span>
                </th>
                <th className="text-center py-3 px-2 border-l border-gray-600/30" title="Soma: T.Corte + T.Intra + T.Entre">
                  <span className="text-sky-400">T.Total</span>
                </th>
                <th className="text-center py-3 px-2" title="% de tempo da máquina parada (Intra Marcadas) em relação ao tempo de corte">
                  <span className="text-rose-400">% Intra/Corte</span>
                </th>
                <th className="text-center py-3 px-2" title="Média de tempo entre marcadas por corte">
                  <span className="text-cyan-400">Méd.E.Marc</span>
                </th>
                <th className="text-center py-3 px-2 text-violet-400" title="Total de pausas legais deduzidas">
                  Deduções
                </th>
                
                {/* VELOCIDADE/PRODUÇÃO */}
                <th className="text-center py-3 px-2 border-l border-gray-600/30" title="Velocidade durante o corte (instantânea, medição real_speed da FILC)">
                  <span className="text-emerald-400">Vel.Real Corte</span>
                </th>
                <th className="text-center py-3 px-2" title="Velocidade média do ciclo de corte (inclui posicionamento; medição avg_speed da FILC)">
                  <span className="text-amber-400">Vel.Média Corte</span>
                </th>
                <th className="text-center py-3 px-2" title="Perímetro total cortado">
                  Perímetro
                </th>
                
                {/* EXTRAS */}
                <th 
                  className="text-center py-3 px-2 border-l border-gray-600/30 cursor-help" 
                  title="Tempo a trabalhar fora do horário (antes 8h / após 17:30)"
                >
                  <span className="border-b border-dotted border-current">Fora Hor.</span>
                </th>
                <th className="text-center py-3 px-2">Horário</th>
                <th className="text-center py-3 px-2 w-12">Acções</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((day, idx) => {
                const overtime = calculateOvertime(day);
                const isSelected = selectedDays.includes(day.date);
                
                return (
                  <tr 
                    key={day.date || idx} 
                    className={`border-t ${t.divider} hover:bg-black/5 dark:hover:bg-white/5 transition-colors cursor-pointer ${isSelected ? 'bg-sky-500/5' : ''}`}
                    onClick={() => onSelectDay(day)}
                  >
                    <td className="py-2 px-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleDaySelection(day.date)}
                        className="rounded"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <span className={`hover:text-sky-500 ${t.text} text-sm`}>
                        {formatDateWithDay(day.date)}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-center font-medium">{day.total_cuts}</td>
                    
                    {/* PERCENTAGENS - com fundo visual e cores dinâmicas */}
                    <td className={`py-2 px-2 text-center font-bold bg-emerald-500/10 border-l border-emerald-500/30 ${getColorHigherBetter(day.efficiency, efficiencyTarget, efficiencyWarning)}`}>
                      <div className="flex items-center justify-center gap-1">
                        {day.efficiency}%
                        {day.efficiency >= efficiencyTarget ? (
                          <CheckCircle className="w-3 h-3" />
                        ) : day.efficiency >= efficiencyWarning ? (
                          <AlertTriangle className="w-3 h-3" />
                        ) : (
                          <AlertTriangle className="w-3 h-3" />
                        )}
                      </div>
                    </td>
                    <td className={`py-2 px-2 text-center font-medium bg-red-500/10 ${getColorLowerBetter(day.pct_parada || 0, machineStoppedTarget, machineStoppedWarning)}`}>
                      {(day.pct_parada || 0).toFixed(1)}%
                    </td>
                    <td className={`py-2 px-2 text-center font-medium bg-cyan-500/10 border-r border-cyan-500/30 ${getColorLowerBetter(day.pct_entre || 0, machineStoppedTarget, machineStoppedWarning)}`}>
                      {(day.pct_entre || 0).toFixed(1)}%
                    </td>
                    
                    {/* TEMPOS — valores BRUTOS (raw), antes de deduzir as pausas legais.
                        DEDUÇÕES (a seguir) mostra as pausas. Real = raw + DEDUÇÕES. */}
                    <td className="py-2 px-2 text-center font-mono text-xs text-sky-400">{formatMinutesToHHMMSS(day.total_cutting_time)}</td>
                    <td
                      className="py-2 px-2 text-center text-amber-400 font-mono text-xs"
                      title={(() => {
                        const raw = day.total_machine_stopped_raw ?? day.total_machine_stopped ?? 0;
                        const ded = day.break_deduct_machine_stopped || 0;
                        const net = Math.max(0, raw - ded);
                        return `Bruto (CRM): ${raw.toFixed(1)}min` + (ded > 0 ? ` − Pausas legais: ${ded}min = Real: ${net.toFixed(1)}min` : '');
                      })()}
                    >
                      {formatMinutesToHHMMSS(day.total_machine_stopped_raw ?? day.total_machine_stopped)}
                    </td>
                    <td
                      className="py-2 px-2 text-center text-cyan-400 font-mono text-xs"
                      title={(() => {
                        const raw = day.total_entre_cortes_raw ?? day.total_entre_cortes ?? 0;
                        const ded = day.break_deduct_between_markers || 0;
                        const net = Math.max(0, raw - ded);
                        return `Bruto (CRM): ${raw.toFixed(1)}min` + (ded > 0 ? ` − Pausas legais: ${ded}min = Real: ${net.toFixed(1)}min` : '');
                      })()}
                    >
                      {formatMinutesToHHMMSS(day.total_entre_cortes_raw ?? day.total_entre_cortes)}
                    </td>
                    <td className="py-2 px-2 text-center text-sky-400 font-mono text-xs border-l border-gray-600/30" title={(() => {
                      const elapsed = (day.last_end_minutes || 0) - (day.first_start_minutes || 0);
                      const breaks = 110;
                      const overtimeGap = day.overtime_gap_outside_hours || 0;
                      const tTotal = Math.max(0, elapsed - breaks - overtimeGap);
                      return `${day.first_start || ''} → ${day.last_end || ''} = ${elapsed.toFixed(0)}min - ${breaks}min pausas${overtimeGap > 0 ? ` - ${overtimeGap.toFixed(0)}min gap turno extra` : ''} = ${tTotal.toFixed(0)}min`;
                    })()}>
                      {(() => {
                        const elapsed = (day.last_end_minutes || 0) - (day.first_start_minutes || 0);
                        const breaks = 110;
                        const overtimeGap = day.overtime_gap_outside_hours || 0;
                        const tTotal = Math.max(0, elapsed - breaks - overtimeGap);
                        return tTotal > 0 ? formatMinutesToHHMMSS(tTotal) : '-';
                      })()}
                    </td>
                    <td className="py-2 px-2 text-center text-rose-400 font-mono text-xs" title="Tempo de máquina parada (Intra Marcadas) ÷ Tempo de corte × 100">
                      {(() => {
                        const cut = day.total_cutting_time || 0;
                        const stop = day.total_machine_stopped_raw ?? day.total_machine_stopped ?? 0;
                        if (cut <= 0) return '-';
                        const pct = (stop / cut) * 100;
                        return `${pct.toFixed(1)}%`;
                      })()}
                    </td>
                    <td className={`py-2 px-2 text-center font-mono text-xs ${day.total_cuts > 0 ? getColorLowerBetter((day.total_entre_cortes_raw ?? day.total_entre_cortes ?? 0) / day.total_cuts, entreCorteTarget, entreCorteWarning) : t.textMut}`} title={`${(day.total_entre_cortes_raw ?? day.total_entre_cortes ?? 0).toFixed(1)} min / ${day.total_cuts} cortes (valor bruto)`}>
                      {day.total_cuts > 0 ? (() => {
                        const avg = (day.total_entre_cortes_raw ?? day.total_entre_cortes ?? 0) / day.total_cuts;
                        const diffPct = entreCorteTarget > 0 ? ((avg - entreCorteTarget) / entreCorteTarget * 100) : 0;
                        return (
                          <span>{formatMinutesToHHMMSS(avg)} <span className={`text-[9px] ${diffPct <= 0 ? 'text-emerald-500' : 'text-red-400'}`}>({diffPct <= 0 ? '' : '+'}{diffPct.toFixed(0)}%)</span></span>
                        );
                      })() : '-'}
                    </td>
                    <td className="py-2 px-2 text-center text-violet-400 font-mono text-xs" title={`Parada: -${day.break_deduct_machine_stopped || 0}min, Entre: -${day.break_deduct_between_markers || 0}min`}>
                      {(day.total_break_deductions || 0) > 0 ? (
                        `-${formatMinutesToHHMMSS(day.total_break_deductions || 0)}`
                      ) : (
                        <span className={t.textMut} title="Sem registos detalhados - reimporte para detetar pausas">-</span>
                      )}
                    </td>
                    
                    {/* VELOCIDADE/PRODUÇÃO */}
                    <td className={`py-2 px-2 text-center text-sm border-l border-gray-600/30 text-emerald-400 font-medium`} title="Velocidade durante o corte (real_speed da FILC)">
                      {(day.avg_real_speed || 0).toFixed(1)} m/min
                    </td>
                    <td className={`py-2 px-2 text-center text-sm text-amber-400 font-medium`} title="Velocidade média do ciclo (avg_speed da FILC, inclui posicionamento)">
                      {(day.avg_avg_speed || 0) > 0 ? `${day.avg_avg_speed.toFixed(1)} m/min` : '-'}
                    </td>
                    <td className="py-2 px-2 text-center text-sm font-mono">
                      {day.total_perimeter ? `${Math.round(day.total_perimeter).toLocaleString('pt-PT')} m` : '-'}
                    </td>
                    
                    {/* EXTRAS */}
                    <td 
                      className={`py-2 px-2 text-center text-xs font-mono border-l border-gray-600/30 ${day.time_outside_hours > 0 ? 'text-amber-500 font-medium' : t.textMut}`}
                      title={`Tempo fora do horário${overtime ? ' (turno extra, gap descontado)' : ''}`}
                    >
                      {day.time_outside_hours > 0 ? formatMinutesToHHMMSS(day.time_outside_hours) : '-'}
                    </td>
                    <td className="py-2 px-2 text-center text-xs">
                      <div className="flex items-center justify-center gap-1">
                        <span>{day.first_start || '-'}</span>
                        <span className={t.textMut}>-</span>
                        <span className={overtime ? 'text-amber-500 font-medium' : ''}>{day.last_end || '-'}</span>
                        {overtime && (
                          <Clock className="w-3 h-3 text-amber-500" title={`+${overtime}min extra`} />
                        )}
                        {day.has_manual_adjustments && (
                          <Edit2 className="w-3 h-3 text-violet-500" title="Tem ajustes manuais" />
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-2 text-center" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => confirmDelete(day.date)}
                        className="p-1 rounded hover:bg-red-500/10 text-red-500"
                        title="Eliminar dia"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className={`flex items-center justify-between px-4 py-3 border-t ${t.divider}`}>
          <div className="flex items-center gap-2">
            <span className={`text-xs ${t.textMut}`}>Mostrar</span>
            {[25, 50, 100].map(size => (
              <button
                key={size}
                onClick={() => { setPageSize(size); setCurrentPage(1); }}
                className={`px-2 py-1 text-xs rounded ${pageSize === size ? 'bg-sky-500 text-white' : `${t.inner} ${t.textMut}`}`}
              >
                {size}
              </button>
            ))}
            <span className={`text-xs ${t.textMut}`}>por página</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs ${t.textMut}`}>
              {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, sorted.length)} de {sorted.length}
            </span>
            {totalPages > 1 && (
              <div className="flex gap-1">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className={`px-2 py-1 text-xs rounded ${t.inner} ${t.textMut} disabled:opacity-30`}
                >
                  Ant.
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className={`px-2 py-1 text-xs rounded ${t.inner} ${t.textMut} disabled:opacity-30`}
                >
                  Seg.
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Selected Day Detail - Full records table */}
      {selectedDay && (
        <DayDetail 
          day={selectedDay} 
          onClose={() => onSelectDay(null)} 
          onClassify={handleOpenClassify}
          onRefresh={onRefresh}
          t={t} 
          settings={settings} 
        />
      )}
    </div>
  );
}

// Day Detail Component - Shows ALL individual records
export function DayDetail({ day, onClose, onClassify, onRefresh, t, settings }) {
  const [notes, setNotes] = React.useState(day?.notes || '');
  const [savingNotes, setSavingNotes] = React.useState(false);
  const [showSubtractModal, setShowSubtractModal] = React.useState(false);
  const [subtractForm, setSubtractForm] = React.useState({ field_type: 'machine_stopped', minutes: '', reason: '' });
  const [subtracting, setSubtracting] = React.useState(false);
  
  // Sync notes when day changes
  React.useEffect(() => { setNotes(day?.notes || ''); }, [day?.date, day?.notes]);
  
  if (!day) return null;

  // Get efficiency target from settings
  const efficiencyTarget = settings?.efficiencyTarget || 35;

  // Extract metrics and records from day object
  const metrics = day.metrics || {};
  const records = day.records || [];
  
  // Get values from metrics (backend returns them there)
  const totalCuts = metrics.total_cuts || day.total_cuts || records.filter(r => !r.is_time_adjustment).length;
  const totalCuttingTime = metrics.total_cutting_time || day.total_cutting_time || 0;
  // Velocidade Real de Corte = velocidade durante o corte real (instantânea) — vem de record.real_speed
  // Velocidade Média de Corte = média do ciclo de corte (inclui movimentos/posicionamentos) — vem de record.avg_speed
  const realCuttingSpeed = metrics.avg_real_speed || day.avg_real_speed || 0;
  const avgCycleSpeed = metrics.avg_avg_speed || day.avg_avg_speed || 0;
  const weightedAvgSpeed = metrics.weighted_avg_speed || day.weighted_avg_speed || 0;
  // legacy alias kept for any place still reading avgSpeed
  const avgSpeed = realCuttingSpeed;
  
  // Manual adjustments tracked separately
  const manualAdjStopped = metrics.manual_adj_machine_stopped || 0;
  const manualAdjBetween = metrics.manual_adj_between_markers || 0;
  
  // Check if breaks were pre-deducted at import time
  const realRecords = records.filter(r => !r.is_time_adjustment);
  const alreadyDeducted = realRecords.some(r => (r.break_deducted_from_stopped || 0) > 0 || (r.break_deducted_from_between || 0) > 0);
  
  // Get break deductions
  const breakDeductStopped = metrics.break_deduct_machine_stopped || day.break_deduct_machine_stopped || 0;
  const breakDeductEntre = metrics.break_deduct_between_markers || day.break_deduct_between_markers || 0;
  
  // Get values from real records (metrics.total_machine_stopped excludes adjustments and excluded records)
  const totalMachineStoppedReal = metrics.total_machine_stopped || day.total_machine_stopped || 0;
  const totalEntreCortesReal = metrics.total_between_markers || day.total_entre_cortes || metrics.total_entre_cortes || 0;
  
  // Calculate ADJUSTED values - same logic as backend summary
  let totalMachineStopped, totalEntreCortes;
  if (alreadyDeducted) {
    // Breaks already in record values - just subtract manual adjustments
    totalMachineStopped = Math.max(0, totalMachineStoppedReal - manualAdjStopped);
    totalEntreCortes = Math.max(0, totalEntreCortesReal - manualAdjBetween);
  } else {
    // Need to apply break deductions + manual adjustments  
    totalMachineStopped = Math.max(0, totalMachineStoppedReal - breakDeductStopped - manualAdjStopped);
    totalEntreCortes = Math.max(0, totalEntreCortesReal - breakDeductEntre - manualAdjBetween);
  }
  
  // Calculate efficiency using same formula as tabela: corte / (corte + parada + entre)
  const totalTempoUtil = totalCuttingTime + totalMachineStopped + totalEntreCortes;
  const efficiency = totalTempoUtil > 0 ? (totalCuttingTime / totalTempoUtil * 100) : 0;
  
  // Percentages for distribution chart (same as table)
  const pctCorte = totalTempoUtil > 0 ? (totalCuttingTime / totalTempoUtil * 100) : 0;
  const pctStopped = totalTempoUtil > 0 ? (totalMachineStopped / totalTempoUtil * 100) : 0;
  const pctEntre = totalTempoUtil > 0 ? (totalEntreCortes / totalTempoUtil * 100) : 0;
  
  const totalRealTime = metrics.total_real_time || day.total_real_time || 0;
  const totalPerimeter = metrics.total_perimeter || day.total_perimeter || 0;
  const firstStart = metrics.first_start || day.first_start || '-';
  const lastEnd = metrics.last_end || day.last_end || '-';
  
  // Detect overtime operations
  const hasOvertime = metrics.has_overtime_shift || false;
  const overtimeStartIndex = metrics.overtime_start_index ?? -1;
  const overtimeRecordCount = hasOvertime && overtimeStartIndex >= 0 ? records.length - overtimeStartIndex : 0;
  
  // Time within working hours (8:00-17:30)
  const cuttingInHours = metrics.cutting_in_hours || day.cutting_in_hours || 0;
  const stoppedInHours = metrics.stopped_in_hours || day.stopped_in_hours || 0;
  const entreInHours = metrics.entre_in_hours || day.entre_in_hours || 0;
  const totalInHours = cuttingInHours + stoppedInHours + entreInHours;
  const efficiencyInHours = totalInHours > 0 ? (cuttingInHours / totalInHours) * 100 : 0;
  const stoppedPctInHours = totalInHours > 0 ? (stoppedInHours / totalInHours) * 100 : 0;
  const entrePctInHours = totalInHours > 0 ? (entreInHours / totalInHours) * 100 : 0;
  
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pt-PT', { 
      weekday: 'long', 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/cutting-simulator/days/${day.date}/notes`, { notes });
      toast.success('Observações guardadas');
    } catch {
      toast.error('Erro ao guardar observações');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleSubtractTime = async () => {
    if (!subtractForm.minutes || subtractForm.minutes <= 0 || !subtractForm.reason) {
      toast.error('Preencha todos os campos');
      return;
    }
    setSubtracting(true);
    try {
      await api.post(`/cutting-simulator/days/${day.date}/subtract-time`, {
        field_type: subtractForm.field_type,
        minutes: parseFloat(subtractForm.minutes),
        reason: subtractForm.reason,
      });
      toast.success(`-${subtractForm.minutes}min ${subtractForm.field_type === 'machine_stopped' ? 'Intra Marc.' : 'Entre Marc.'}`);
      setShowSubtractModal(false);
      setSubtractForm({ field_type: 'machine_stopped', minutes: '', reason: '' });
      if (onRefresh) onRefresh();
    } catch {
      toast.error('Erro ao subtrair tempo');
    } finally {
      setSubtracting(false);
    }
  };

  const subtractReasons = settings?.anomalousReasons?.length > 0 
    ? settings.anomalousReasons 
    : [{ id: 'lunch', name: 'Pausa Almoço' }, { id: 'training', name: 'Formação' }, { id: 'breakdown', name: 'Avaria' }, { id: 'setup', name: 'Setup/Preparação' }, { id: 'maintenance', name: 'Manutenção' }, { id: 'other', name: 'Outro' }];

  return (
    <div className={`${t.card} rounded-lg p-6`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-sky-500" />
          <h3 className={`text-lg font-semibold ${t.text}`}>
            Detalhe: {day.date}
          </h3>
          {hasOvertime && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 text-amber-500 border border-amber-500/20">
              <Zap className="w-3 h-3" />
              {overtimeRecordCount} em turno extra
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className={`p-2 rounded-lg ${t.inner} hover:opacity-80`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className={`p-4 rounded-lg ${t.inner}`}>
          <div className={`text-xs ${t.textMut} mb-1`}>Cortes</div>
          <div className={`text-2xl font-bold ${t.text}`}>{totalCuts}</div>
        </div>
        <div className={`p-4 rounded-lg ${t.inner}`} title="Velocidade durante o corte (instantânea, da medição real_speed da FILC)">
          <div className={`text-xs ${t.textMut} mb-1`}>Vel. Real de Corte</div>
          <div className={`text-2xl font-bold text-emerald-500`}>
            {realCuttingSpeed.toFixed(1)} m/min
          </div>
        </div>
        <div className={`p-4 rounded-lg ${t.inner}`} title="Velocidade média do ciclo de corte (inclui posicionamento, da medição avg_speed da FILC)">
          <div className={`text-xs ${t.textMut} mb-1`}>Vel. Média de Corte</div>
          <div className={`text-2xl font-bold text-amber-500`}>
            {avgCycleSpeed > 0 ? avgCycleSpeed.toFixed(1) : '-'} m/min
          </div>
        </div>
        <div className={`p-4 rounded-lg ${t.inner}`}>
          <div className={`text-xs ${t.textMut} mb-1`}>Tempo Corte</div>
          <div className={`text-2xl font-bold text-sky-500`}>{formatMinutesToHHMMSS(totalCuttingTime)}</div>
        </div>
      </div>

      {/* Observations + Subtract Time */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className={`flex-1 p-4 rounded-lg ${t.inner}`}>
          <div className={`text-xs font-semibold ${t.textMut} mb-2`}>Observações do Dia</div>
          <div className="flex gap-2">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Adicionar observações para este dia..."
              rows={2}
              className={`flex-1 px-3 py-2 text-sm rounded-lg ${t.inputBg} ${t.card} ${t.text} resize-none`}
              data-testid="day-notes-input"
            />
            <button
              onClick={saveNotes}
              disabled={savingNotes}
              className="px-3 py-2 bg-sky-500 text-white text-xs font-medium rounded-lg hover:bg-sky-600 disabled:opacity-50 self-end"
              data-testid="save-notes-btn"
            >
              {savingNotes ? '...' : 'Guardar'}
            </button>
          </div>
        </div>
        <div className={`p-4 rounded-lg ${t.inner} flex flex-col justify-center`}>
          <div className={`text-xs font-semibold ${t.textMut} mb-2`}>Ajustar Tempos</div>
          <button
            onClick={() => setShowSubtractModal(true)}
            className="px-4 py-2 bg-amber-500 text-white text-xs font-medium rounded-lg hover:bg-amber-600 flex items-center gap-1"
            data-testid="subtract-time-btn"
          >
            <Clock className="w-3.5 h-3.5" /> Retirar Tempo
          </button>
        </div>
      </div>

      {/* Subtract Time Modal */}
      {showSubtractModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${t.card} rounded-xl shadow-xl w-full max-w-md`}>
            <div className="p-5 border-b border-gray-200 dark:border-zinc-700">
              <h3 className={`text-lg font-semibold ${t.text}`}>Retirar Tempo</h3>
              <p className={`text-xs ${t.textMut} mt-1`}>Subtrair tempo ao Intra Marcadas ou Entre Marcadas do dia {day.date}</p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className={`block text-sm font-medium ${t.text} mb-1`}>Tipo</label>
                <select
                  value={subtractForm.field_type}
                  onChange={(e) => setSubtractForm(prev => ({ ...prev, field_type: e.target.value }))}
                  className={`w-full px-3 py-2 rounded-lg ${t.inputBg} ${t.card} ${t.text}`}
                >
                  <option value="machine_stopped">Intra Marcadas (Máq. Parada)</option>
                  <option value="between_markers">Entre Marcadas</option>
                </select>
              </div>
              <div>
                <label className={`block text-sm font-medium ${t.text} mb-1`}>Minutos a Retirar</label>
                <input
                  type="number"
                  min="0.1"
                  step="0.1"
                  value={subtractForm.minutes}
                  onChange={(e) => setSubtractForm(prev => ({ ...prev, minutes: e.target.value }))}
                  placeholder="Ex: 45"
                  className={`w-full px-3 py-2 rounded-lg ${t.inputBg} ${t.card} ${t.text}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${t.text} mb-1`}>Motivo</label>
                <select
                  value={subtractForm.reason}
                  onChange={(e) => setSubtractForm(prev => ({ ...prev, reason: e.target.value }))}
                  className={`w-full px-3 py-2 rounded-lg ${t.inputBg} ${t.card} ${t.text}`}
                >
                  <option value="">Selecionar...</option>
                  {subtractReasons.map(r => (
                    <option key={r.id || r.name} value={r.name}>{r.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="p-5 border-t border-gray-200 dark:border-zinc-700 flex justify-end gap-3">
              <button onClick={() => setShowSubtractModal(false)} className={`px-4 py-2 rounded-lg ${t.inner} ${t.textSec}`}>Cancelar</button>
              <button onClick={handleSubtractTime} disabled={subtracting} className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 font-medium">
                {subtracting ? 'A guardar...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Working Hours Time Distribution (8:00-17:30) */}
      <div className={`p-4 rounded-lg ${t.inner} mb-6`}>
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-4 h-4 text-sky-500" />
          <h4 className={`text-sm font-semibold ${t.text}`}>Distribuição de Tempo (Horário Laboral 8:00-17:30)</h4>
        </div>
        
        {/* Visual Bar */}
        <div className="h-8 rounded-lg overflow-hidden flex mb-4" title={`Total: ${formatMinutesToHHMMSS(totalTempoUtil)}`}>
          <div 
            className="bg-emerald-500 flex items-center justify-center text-white text-xs font-medium"
            style={{ width: `${pctCorte}%` }}
            title={`Corte: ${formatMinutesToHHMMSS(totalCuttingTime)} (${pctCorte.toFixed(1)}%)`}
          >
            {pctCorte > 10 && `${pctCorte.toFixed(1)}%`}
          </div>
          <div 
            className="bg-red-500 flex items-center justify-center text-white text-xs font-medium"
            style={{ width: `${pctStopped}%` }}
            title={`Parada: ${formatMinutesToHHMMSS(totalMachineStopped)} (${pctStopped.toFixed(1)}%)`}
          >
            {pctStopped > 10 && `${pctStopped.toFixed(1)}%`}
          </div>
          <div 
            className="bg-cyan-500 flex items-center justify-center text-white text-xs font-medium"
            style={{ width: `${pctEntre}%` }}
            title={`Entre: ${formatMinutesToHHMMSS(totalEntreCortes)} (${pctEntre.toFixed(1)}%)`}
          >
            {pctEntre > 10 && `${pctEntre.toFixed(1)}%`}
          </div>
        </div>
        
        {/* Legend */}
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="flex items-center justify-center gap-2 mb-1">
              <div className="w-3 h-3 rounded bg-emerald-500"></div>
              <span className={`text-xs ${t.textMut}`}>Corte</span>
            </div>
            <div className="text-lg font-bold text-emerald-500">{formatMinutesToHHMMSS(totalCuttingTime)}</div>
            <div className={`text-xs ${t.textMut}`}>{pctCorte.toFixed(1)}%</div>
          </div>
          <div>
            <div className="flex items-center justify-center gap-2 mb-1">
              <div className="w-3 h-3 rounded bg-red-500"></div>
              <span className={`text-xs ${t.textMut}`}>Máq. Parada</span>
            </div>
            <div className="text-lg font-bold text-red-500">{formatMinutesToHHMMSS(totalMachineStopped)}</div>
            <div className={`text-xs ${t.textMut}`}>{pctStopped.toFixed(1)}%</div>
          </div>
          <div>
            <div className="flex items-center justify-center gap-2 mb-1">
              <div className="w-3 h-3 rounded bg-cyan-500"></div>
              <span className={`text-xs ${t.textMut}`}>Entre Tarefas</span>
            </div>
            <div className="text-lg font-bold text-cyan-500">{formatMinutesToHHMMSS(totalEntreCortes)}</div>
            <div className={`text-xs ${t.textMut}`}>{pctEntre.toFixed(1)}%</div>
          </div>
        </div>
      </div>

      {hasOvertime && (
        <div className="mb-3 p-2 rounded-lg bg-amber-500/5 border border-amber-500/20 text-xs text-amber-500 flex items-center gap-2">
          <Zap className="w-3 h-3" />
          <span>Operações após a linha amarela são de turno extra (após 17:30 com pausa &gt;1h)</span>
        </div>
      )}

      {/* Time Adjustments Summary - Shows all deductions applied */}
      <TimeAdjustmentsSummary 
        metrics={metrics} 
        records={records} 
        hasOvertime={hasOvertime} 
        overtimeStartIndex={overtimeStartIndex} 
        t={t}
        finalValues={{ totalCuttingTime, totalMachineStopped, totalEntreCortes, pctCorte, pctStopped, pctEntre }}
      />
      
      {/* Individual Records Table */}
      {records.length > 0 ? (
        <div className="overflow-x-auto mb-6">
          <table className="w-full text-xs">
            <thead>
              <tr className={`${t.textMut} ${t.divider} border-b`}>
                <th className="text-left py-2 px-2">Data</th>
                <th className="text-left py-2 px-2">Início</th>
                <th className="text-left py-2 px-2">Fim</th>
                <th className="text-right py-2 px-2">Exec.</th>
                <th className="text-right py-2 px-2 text-sky-500">Corte</th>
                <th className="text-right py-2 px-2 text-red-400">Parada</th>
                <th className="text-right py-2 px-2 text-cyan-400">Entre Marc.</th>
                <th className="text-right py-2 px-2">Perímetro</th>
                <th className="text-right py-2 px-2">Vel. Real</th>
                <th className="text-right py-2 px-2">Vel. Média</th>
                <th className="text-left py-2 px-2">Plano</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r, i) => {
                const isOvertime = hasOvertime && overtimeStartIndex >= 0 && i >= overtimeStartIndex;
                const isOvertimeStart = hasOvertime && overtimeStartIndex >= 0 && i === overtimeStartIndex;
                // Check if THIS record's between_markers represents the shift transition gap
                // (i.e., this is the last record before overtime starts)
                const isShiftTransitionGap = hasOvertime && overtimeStartIndex >= 0 && i === overtimeStartIndex - 1;
                
                const recordDate = new Date(day.date);
                const dateFormatted = `${recordDate.getDate().toString().padStart(2, '0')}/${(recordDate.getMonth() + 1).toString().padStart(2, '0')}/${recordDate.getFullYear()}`;
                
                // Check for anomalous times (>20 min)
                const machineStopped = parseFloat(r.machine_stopped || 0);
                const betweenMarkers = parseFloat(r.between_markers || 0);
                const hasAnomalousStopped = machineStopped > 20;
                // Don't flag between_markers as anomalous if it's a shift transition gap
                const hasAnomalousBetween = betweenMarkers > 20 && !isShiftTransitionGap;
                const hasAnyAnomalous = hasAnomalousStopped || hasAnomalousBetween;
                
                // Check if this record contains a break deduction
                const breaksInfo = metrics.breaks_info || {};
                const breakHere = Object.entries(breaksInfo)
                  .filter(([, info]) => info?.detected && info.record_indices?.includes(i))
                  .map(([key, info]) => ({ key, name: key === 'lunch' ? 'Almoço' : key === 'break1' ? 'P.Manhã' : 'P.Tarde', minutes: info.deducted, from: info.deducted_from }));
                
                // Check if deductions were applied
                const deductedStopped = r.break_deducted_from_stopped || 0;
                const deductedBetween = r.break_deducted_from_between || 0;
                const hasDeductions = deductedStopped > 0 || deductedBetween > 0;
                
                // Check for anomalous classifications
                const classifiedStopped = r.anomalous_classification_stopped;
                const classifiedBetween = r.anomalous_classification_between;
                
                return (
                  <React.Fragment key={`record-${r.start_time}-${i}`}>
                    {isOvertimeStart && (
                      <tr>
                        <td colSpan={11} className="py-1">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-px bg-amber-500/50"></div>
                            <span className="text-[10px] uppercase tracking-wider text-amber-500 font-medium flex items-center gap-1">
                              <Zap className="w-3 h-3" />
                              TURNO EXTRA
                            </span>
                            <div className="flex-1 h-px bg-amber-500/50"></div>
                          </div>
                        </td>
                      </tr>
                    )}
                    <tr className={`${t.divider} border-b hover:bg-black/5 dark:hover:bg-white/5 ${
                      r.is_time_adjustment ? 'bg-violet-500/10' :
                      isOvertime ? 'bg-amber-500/5' : 
                      hasAnyAnomalous && !hasDeductions && !classifiedStopped && !classifiedBetween ? 'bg-red-500/5' : ''
                    }`}>
                      <td className={`py-1.5 px-2 ${t.textMut} whitespace-nowrap`}>
                        {hasAnyAnomalous && !hasDeductions && !classifiedStopped && !classifiedBetween && (
                          <AlertTriangle className="w-3 h-3 inline mr-1 text-amber-500" title="Tempo anormal detectado" />
                        )}
                        {dateFormatted}
                      </td>
                      <td className={`py-1.5 px-2 ${t.textBody} ${isOvertime ? 'text-amber-500' : ''}`}>
                        {isOvertime && <Zap className="w-3 h-3 inline mr-1 text-amber-500" />}
                        {r.start_time}
                      </td>
                      <td className={`py-1.5 px-2 ${t.textBody} ${isOvertime ? 'text-amber-500' : ''}`}>{r.end_time || '-'}</td>
                      <td className={`py-1.5 px-2 text-right ${t.textBody}`} title={`${parseFloat(r.execution_time || 0).toFixed(2)} min`}>
                        {formatMinutesToMMSS(parseFloat(r.execution_time || 0))}
                      </td>
                      <td className="py-1.5 px-2 text-right text-sky-500 font-medium" title={`${parseFloat(r.cutting_time || 0).toFixed(2)} min`}>
                        {formatMinutesToMMSS(parseFloat(r.cutting_time || 0))}
                      </td>
                      <td className={`py-1.5 px-2 text-right font-medium ${hasAnomalousStopped ? 'text-red-600 font-bold' : 'text-red-400'}`}>
                        <span 
                          className={`${hasAnomalousStopped && !classifiedStopped ? 'cursor-pointer hover:underline' : ''}`}
                          onClick={() => hasAnomalousStopped && !classifiedStopped && onClassify && onClassify({
                            record_index: i,
                            field_type: 'machine_stopped',
                            machine_stopped: machineStopped,
                            start_time: r.start_time,
                            end_time: r.end_time
                          }, day)}
                          title={(() => {
                            const bk = breakHere.filter(b => b.from === 'machine_stopped');
                            const base = `${machineStopped.toFixed(2)} min`;
                            if (bk.length > 0) return `${base} (inclui ${bk.map(b => `${b.name} ${b.minutes}min`).join(', ')})`;
                            return hasAnomalousStopped && !classifiedStopped ? `Clique para classificar (${base})` : base;
                          })()}
                        >
                          {hasAnomalousStopped && !classifiedStopped && <Edit2 className="w-3 h-3 inline mr-0.5 text-amber-500" />}
                          {formatMinutesToMMSS(machineStopped)}
                        </span>
                        {breakHere.some(b => b.from === 'machine_stopped') && (
                          <span className="block text-[9px] text-violet-500" title={breakHere.filter(b => b.from === 'machine_stopped').map(b => `${b.name}: -${b.minutes}min`).join(', ')}>
                            {breakHere.filter(b => b.from === 'machine_stopped').map(b => `${b.name} -${b.minutes}min`).join(' ')}
                          </span>
                        )}
                        {classifiedStopped && (
                          <CheckCircle className="w-3 h-3 inline ml-0.5 text-emerald-500" title="Classificado" />
                        )}
                      </td>
                      <td className={`py-1.5 px-2 text-right font-medium ${hasAnomalousBetween ? 'text-cyan-600 font-bold' : isShiftTransitionGap ? 'text-amber-500' : 'text-cyan-400'}`}>
                        <span 
                          className={`${hasAnomalousBetween && !classifiedBetween ? 'cursor-pointer hover:underline' : ''}`}
                          onClick={() => hasAnomalousBetween && !classifiedBetween && onClassify && onClassify({
                            record_index: i,
                            field_type: 'between_markers',
                            between_markers: betweenMarkers,
                            start_time: r.start_time,
                            end_time: r.end_time
                          }, day)}
                          title={(() => {
                            const bk = breakHere.filter(b => b.from !== 'machine_stopped');
                            const base = `${betweenMarkers.toFixed(2)} min`;
                            if (isShiftTransitionGap) return `Transição para turno extra (${base})`;
                            if (bk.length > 0) return `${base} (inclui ${bk.map(b => `${b.name} ${b.minutes}min`).join(', ')})`;
                            return hasAnomalousBetween && !classifiedBetween ? `Clique para classificar (${base})` : base;
                          })()}
                        >
                          {hasAnomalousBetween && !classifiedBetween && <Edit2 className="w-3 h-3 inline mr-0.5 text-amber-500" />}
                          {isShiftTransitionGap && <Zap className="w-3 h-3 inline mr-0.5 text-amber-500" title="Transição turno" />}
                          {formatMinutesToMMSS(betweenMarkers)}
                        </span>
                        {breakHere.some(b => b.from !== 'machine_stopped') && (
                          <span className="block text-[9px] text-violet-500" title={breakHere.filter(b => b.from !== 'machine_stopped').map(b => `${b.name}: -${b.minutes}min`).join(', ')}>
                            {breakHere.filter(b => b.from !== 'machine_stopped').map(b => `${b.name} -${b.minutes}min`).join(' ')}
                          </span>
                        )}
                        {classifiedBetween && (
                          <CheckCircle className="w-3 h-3 inline ml-0.5 text-emerald-500" title="Classificado" />
                        )}
                      </td>
                      <td className={`py-1.5 px-2 text-right ${t.textBody}`}>{parseFloat(r.perimeter || 0).toFixed(1)}m</td>
                      <td className={`py-1.5 px-2 text-right ${t.textBody}`}>{parseFloat(r.real_speed || 0).toFixed(2)}</td>
                      <td className={`py-1.5 px-2 text-right ${t.textBody}`}>{parseFloat(r.avg_speed || 0).toFixed(2)}</td>
                      <td className={`py-1.5 px-2 ${t.textMut} whitespace-nowrap text-xs`} title={r.plan_name || ''}>{r.plan_name || '-'}</td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={`text-center py-8 ${t.textMut} mb-6`}>
          <p>Nenhum registo individual disponível para este dia.</p>
          <p className="text-xs mt-1">Os dados podem ter sido importados num formato resumido.</p>
        </div>
      )}
      
      {/* Legend for anomalous indicators */}
      {records.length > 0 && records.some(r => (parseFloat(r.machine_stopped || 0) > 20 || parseFloat(r.between_markers || 0) > 20)) && (
        <div className={`mb-4 p-3 rounded-lg ${t.inner} border border-amber-500/20`}>
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1">
              <AlertTriangle className="w-3 h-3 text-amber-500" />
              <span className={t.textMut}>Tempo &gt;20min (classificar)</span>
            </div>
            <div className="flex items-center gap-1">
              <CheckCircle className="w-3 h-3 text-emerald-500" />
              <span className={t.textMut}>Classificado</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-emerald-500">(-XX)</span>
              <span className={t.textMut}>Minutos extraídos</span>
            </div>
          </div>
        </div>
      )}
      
      {/* OBSERVAÇÕES DOS CÁLCULOS - Compacto */}
      <div className={`p-4 rounded-lg ${t.card} border border-violet-500/30`}>
        <div className="flex items-center gap-2 mb-3">
          <FileText className="w-4 h-4 text-violet-400" />
          <h4 className={`text-sm font-bold ${t.text}`}>Resumo de Cálculos</h4>
        </div>
        
        {/* Timeline visual do dia de trabalho */}
        <div className={`mb-4 p-3 rounded-lg ${t.inner}`}>
          <div className="flex items-center justify-between mb-2">
            <span className={`text-xs ${t.textMut}`}>Timeline do Dia ({firstStart} - {lastEnd})</span>
            <span className={`text-xs ${t.textMut}`}>Total: {formatMinutesToHHMMSS(totalRealTime)}</span>
          </div>
          
          {/* Timeline bar */}
          <div className="relative h-6 rounded bg-gray-700/50 overflow-hidden">
            {/* Work hours background (8:00-17:30) */}
            <div 
              className="absolute h-full bg-gray-600/30" 
              style={{ 
                left: `${Math.max(0, ((480 - 420) / 660) * 100)}%`,
                width: `${Math.min(100, ((1050 - 480) / 660) * 100)}%`
              }}
              title="Horário Laboral (8:00-17:30)"
            />
            
            {/* Morning break marker (10:00-10:10) - 10 min proporcional */}
            {metrics.breaks_info?.break1 && (
              <div 
                className="absolute h-full bg-amber-500/80" 
                style={{ 
                  left: `${((600 - 420) / 660) * 100}%`,
                  width: `${(10 / 660) * 100}%`,
                  minWidth: '4px'
                }}
                title="Pausa Manhã (10:00-10:10)"
              />
            )}
            
            {/* Lunch break marker (12:00-13:30) - 90 min proporcional */}
            {metrics.breaks_info?.lunch && (
              <div 
                className="absolute h-full bg-orange-500/60" 
                style={{ 
                  left: `${((720 - 420) / 660) * 100}%`,
                  width: `${(90 / 660) * 100}%`
                }}
                title="Almoço (12:00-13:30)"
              />
            )}
            
            {/* Afternoon break marker (16:00-16:10) - 10 min proporcional */}
            {metrics.breaks_info?.break2 && (
              <div 
                className="absolute h-full bg-amber-500/80" 
                style={{ 
                  left: `${((960 - 420) / 660) * 100}%`,
                  width: `${(10 / 660) * 100}%`,
                  minWidth: '4px'
                }}
                title="Pausa Tarde (16:00-16:10)"
              />
            )}
            
            {/* Overtime marker */}
            {hasOvertime && (
              <div 
                className="absolute h-full bg-amber-500/40 right-0" 
                style={{ width: `${Math.max(5, ((metrics.overtime_gap_minutes || 0) / 660) * 100)}%` }}
                title="Turno Extra"
              />
            )}
            
            {/* Time labels */}
            <div className="absolute inset-0 flex items-center justify-between px-2 text-[9px] text-white/70">
              <span>7:00</span>
              <span>10:00</span>
              <span>12:00</span>
              <span>14:00</span>
              <span>17:30</span>
            </div>
          </div>
          
          {/* Legend */}
          <div className="flex flex-wrap gap-3 mt-2 text-[10px]">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded bg-gray-600/50"></div>
              <span className={t.textMut}>Horário Laboral</span>
            </div>
            {metrics.breaks_info?.break1 && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded bg-amber-500"></div>
                <span className={t.textMut}>Pausa Manhã</span>
              </div>
            )}
            {metrics.breaks_info?.lunch && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded bg-orange-500"></div>
                <span className={t.textMut}>Almoço</span>
              </div>
            )}
            {metrics.breaks_info?.break2 && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded bg-amber-500"></div>
                <span className={t.textMut}>Pausa Tarde</span>
              </div>
            )}
            {hasOvertime && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded bg-amber-500/60"></div>
                <span className={t.textMut}>Turno Extra</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Valores FINAIS em grid compacto com percentagens (após deduções) */}
        {(() => {
          // Usar valores FINAIS (ajustados) para o cálculo de eficiência
          const finalStopped = totalMachineStopped; // Já é o valor ajustado
          const finalEntre = totalEntreCortes; // Já é o valor ajustado
          const totalFinal = totalCuttingTime + finalStopped + finalEntre;
          const pctCorte = totalFinal > 0 ? (totalCuttingTime / totalFinal * 100) : 0;
          const pctParada = totalFinal > 0 ? (finalStopped / totalFinal * 100) : 0;
          const pctEntre = totalFinal > 0 ? (finalEntre / totalFinal * 100) : 0;
          
          return (
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="text-center p-2 rounded bg-sky-500/10">
                <div className="text-sm font-bold text-sky-400">{formatMinutesToHHMMSS(totalCuttingTime)}</div>
                <div className="text-[10px] text-sky-400/70">Corte ({pctCorte.toFixed(1)}%)</div>
              </div>
              <div className="text-center p-2 rounded bg-red-500/10">
                <div className="text-sm font-bold text-red-400">{formatMinutesToHHMMSS(finalStopped)}</div>
                <div className="text-[10px] text-red-400/70">Parada Ajust. ({pctParada.toFixed(1)}%)</div>
              </div>
              <div className="text-center p-2 rounded bg-cyan-500/10">
                <div className="text-sm font-bold text-cyan-400">{formatMinutesToHHMMSS(finalEntre)}</div>
                <div className="text-[10px] text-cyan-400/70">Entre Ajust. ({pctEntre.toFixed(1)}%)</div>
              </div>
            </div>
          );
        })()}
        
        {/* Pausas detectadas - lista inline */}
        {metrics.breaks_info && Object.keys(metrics.breaks_info).length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-xs mb-3">
            <span className={t.textMut}>Pausas:</span>
            {Object.entries(metrics.breaks_info).map(([key, info]) => (
              info.deducted > 0 && (
                <span key={key} className="px-2 py-0.5 rounded bg-violet-500/20 text-violet-300">
                  {info.name}: -{formatMinutesToHHMMSS(info.deducted)}
                </span>
              )
            ))}
          </div>
        )}
        
        {/* Turno extra */}
        {hasOvertime && (
          <div className="flex items-center gap-2 text-xs mb-3 px-2 py-1 rounded bg-amber-500/10">
            <Zap className="w-3 h-3 text-amber-500" />
            <span className="text-amber-400">Turno Extra: gap de {formatMinutesToHHMMSS(metrics.overtime_gap_minutes || 0)} excluído</span>
          </div>
        )}
        
        {/* Valores finais */}
        <div className="flex items-center justify-between pt-2 border-t border-emerald-500/30">
          <span className={`text-xs ${t.textMut}`}>Valores Finais (para cálculo):</span>
          <div className="flex items-center gap-3 text-xs">
            <span className="text-red-400 font-medium">Parada: {formatMinutesToHHMMSS(totalMachineStopped)}</span>
            <span className="text-cyan-400 font-medium">Entre: {formatMinutesToHHMMSS(totalEntreCortes)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DailyAnalysisView;
